<template>
  <div class="dash-grid">
      <!-- Location -->
      <LocationCard
        v-if="shouldShowMap"
        class="span-1" 
        :isEnabled="true"
        :deviceName="deviceName"
        :location="location"
        @refresh="emit('refresh')"
        @center="emit('center')"
        @open-modal="showCoordModal = true"
      />

      <!-- Sensor Status Card -->
      <SensorStatusCard
        v-if="shouldShowMap"
        class="span-3"
        :deviceId="deviceId"
      />

      <LocationModal
        v-if="showCoordModal"
        :deviceName="deviceName"
        :deviceId="deviceId"
        :location="location"
        @close="showCoordModal = false"
        @copy="emit('copyCoords')"
        @saved="emit('refresh')"
      />

      <!-- Config Modal -->
      <EnvironmentConfigModal
        v-if="showConfigModal"
        :device-id="deviceId"
        @close="showConfigModal = false"
        @saved="emit('refresh')"
      />

      <!-- Temperature Card -->
      <THChartCard
        class="card span-2"
        title="Current Temperature"
        type="temp"
        :is-enabled="true"
        :series-t="seriesT"
        :series-h="[]"
        :current-temp="currentTempC"
        :last-update-ago="lastUpdateAgo"
        :current-range="chartRange"
        :temp-min="tempMin"
        :temp-max="tempMax"
        :loading="loading"
        @update:range="emit('update:range', $event)"
        @refresh="emit('refresh')"
        @config="showConfigModal = true"
      />

      <!-- Humidity Card -->
      <THChartCard
        class="card span-2"
        title="Current Humidity"
        type="hum"
        :is-enabled="true"
        :series-t="[]"
        :series-h="seriesH"
        :current-hum="outsideHum"
        :last-update-ago="lastUpdateAgo"
        :current-range="chartRange"
        :hum-min="humMin"
        :hum-max="humMax"
        :loading="loading"
        @update:range="emit('update:range', $event)"
        @refresh="emit('refresh')"
        @config="showConfigModal = true"
      />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import LocationCard from "@/components/data/LocationCard.vue";
import LocationModal from "@/components/data/LocationModal.vue";
import EnvironmentConfigModal from "@/components/data/EnvironmentConfigModal.vue"; // 👈 New Import
import StatCard from "@/components/ui/StatCard.vue";
import THChartCard from "@/components/data/THChartCard.vue";
import SensorStatusCard from "@/components/data/SensorStatusCard.vue";

const props = defineProps<{
    deviceId: number; // 👈 Ensure we have deviceId
    deviceName: string;
    location: [number, number] | undefined;
    currentTempC: number;
    outsideHum: number;
    lastUpdateAgo: string;
    chartRange: string;
    seriesT: any[];
    seriesH: any[];
    showMap?: boolean;
    tempMin?: number;
    tempMax?: number;
    humMin?: number;
    humMax?: number;
    loading?: boolean;
}>();

const emit = defineEmits(['refresh', 'center', 'copyCoords', 'update:range']);

const showCoordModal = ref(false);
const showConfigModal = ref(false);

const shouldShowMap = computed(() => props.showMap !== false); // Default true

/* precisioni helpers */
const TEMP_PRECISION = 1;
const HUM_PRECISION = 0;
function floorToDecimals(n: number, d = 1) {
  const k = Math.pow(10, d);
  return Math.floor(n * k) / k;
}
const formatTempC = (n: number) => `${floorToDecimals(n, TEMP_PRECISION).toFixed(TEMP_PRECISION)}°C`;
const formatHum = (n: number) => `${Math.floor(n).toFixed(HUM_PRECISION)}%`;

</script>

<style scoped src="@/assets/css/data.css"></style>
<style scoped>
.section-header-main {
  margin-bottom: 2px; /* Fine tune spacing for this grid */
}
.settings-btn-main {
  appearance: none;
  border: 1px solid #2a2f34;
  background: #141920;
  color: #cbd5df;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  cursor: pointer;
  line-height: 26px;
  font-size: 1.1rem;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.settings-btn-main:hover {
  background: #1c2229;
  border-color: #22c55e;
  color: #fff;
  transform: translateY(-1px);
}
</style>
